/**
 * Maestros · Productos — filtro del maestro de artículos contra ACSIS.
 *
 * Dos criterios combinados con AND (SKU + nombre) y un solo botón Buscar:
 * el back filtra sobre el maestro completo, porque los endpoints de listado
 * de ACSIS ignoran cualquier parámetro de filtro.
 *
 * De la fila se pasa a la ficha completa (`/api/productos/buscar?sku=`), que
 * trae datos que el listado no tiene: bloque `ventas`, todos los flags y
 * clasificadores.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2, ArrowLeft, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { buscarProductoPorSku, listarProductos } from '@/api/endpoints/producto-acsis';
import type { ArticuloAcsis, ArticuloListItem } from '@/api/contracts/producto-acsis';

export function ProductosTab() {
  const [sku, setSku] = useState('');
  const [nombre, setNombre] = useState('');
  /** Filtro efectivamente aplicado (se congela al apretar Buscar). */
  const [filtro, setFiltro] = useState<{ sku: string; nombre: string } | null>(null);
  const [seleccionado, setSeleccionado] = useState<string | null>(null);

  const listado = useQuery({
    queryKey: ['productos-listar', filtro?.sku ?? '', filtro?.nombre ?? ''],
    queryFn: () => listarProductos({ sku: filtro?.sku, nombre: filtro?.nombre }),
    enabled: filtro !== null,
    retry: false,
  });

  const detalle = useQuery({
    queryKey: ['producto-acsis', seleccionado],
    queryFn: () => buscarProductoPorSku(seleccionado ?? ''),
    enabled: Boolean(seleccionado),
    retry: false,
  });

  // Atajo del flujo anterior (escribir el SKU exacto y ver la ficha): si el
  // filtro deja una sola coincidencia, la abrimos sin obligar al click.
  const items = listado.data?.items;
  useEffect(() => {
    const unico = items?.length === 1 ? items[0] : null;
    if (unico?.sku) setSeleccionado(unico.sku);
  }, [items]);

  const onBuscar = (e: React.FormEvent) => {
    e.preventDefault();
    setSeleccionado(null);
    setFiltro({ sku: sku.trim(), nombre: nombre.trim() });
  };

  const onLimpiar = () => {
    setSku('');
    setNombre('');
    setFiltro(null);
    setSeleccionado(null);
  };

  const hayTexto = sku.trim() !== '' || nombre.trim() !== '';

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4">
          <form onSubmit={onBuscar}>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="producto-sku" className="text-xs text-gray-600">
                  SKU
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="producto-sku"
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                    placeholder="Ej. PRPT0052"
                    className="pl-9 font-mono"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="producto-nombre" className="text-xs text-gray-600">
                  Nombre
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="producto-nombre"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    placeholder="Ej. BOSENTAC"
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
              <p className="text-[11px] text-gray-500">
                Los dos campos se combinan: se listan los artículos que cumplen ambos.
              </p>
              {/* Acciones a la derecha: Limpiar como secundaria, Buscar al borde. */}
              <div className="ml-auto flex items-center gap-2">
                {(hayTexto || filtro) && (
                  <Button type="button" variant="ghost" onClick={onLimpiar} className="text-gray-600">
                    <X className="mr-1 h-4 w-4" />
                    Limpiar
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={listado.isFetching}
                  className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
                >
                  {listado.isFetching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  <span className="ml-1.5">Buscar</span>
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {listado.error ? (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">
            {listado.error.message}
          </AlertDescription>
        </Alert>
      ) : listado.isLoading ? (
        <EstadoCargando texto="Consultando maestro del ERP..." />
      ) : !listado.data ? (
        <Card>
          <CardContent className="py-8 text-center text-xs text-gray-500">
            Ingresá un SKU, un nombre (o los dos) y presioná <b>Buscar</b> para
            filtrar el maestro de artículos del ERP.
          </CardContent>
        </Card>
      ) : seleccionado ? (
        <FichaSeleccionada
          onVolver={() => setSeleccionado(null)}
          cantidad={listado.data.total}
          detalle={detalle}
        />
      ) : (
        <ResultadosProductos
          items={listado.data.items}
          total={listado.data.total}
          truncado={listado.data.truncado}
          totalMaestro={listado.data.totalMaestro}
          onSeleccionar={setSeleccionado}
        />
      )}
    </div>
  );
}

function EstadoCargando({ texto }: { texto: string }) {
  return (
    <div className="flex items-center justify-center py-12 text-gray-600">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
      <span>{texto}</span>
    </div>
  );
}

/** Ficha del artículo elegido + vuelta a la tabla de resultados. */
function FichaSeleccionada({
  onVolver,
  cantidad,
  detalle,
}: {
  onVolver: () => void;
  cantidad: number;
  detalle: {
    data?: { articulo: ArticuloAcsis };
    isLoading: boolean;
    error: Error | null;
  };
}) {
  return (
    <div className="space-y-3">
      <Button
        type="button"
        variant="ghost"
        onClick={onVolver}
        className="h-8 px-2 text-xs text-everex-azul hover:bg-everex-azul-bg"
      >
        <ArrowLeft className="mr-1 h-3.5 w-3.5" />
        Volver a los resultados ({cantidad})
      </Button>
      {detalle.error ? (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">{detalle.error.message}</AlertDescription>
        </Alert>
      ) : detalle.isLoading || !detalle.data ? (
        <EstadoCargando texto="Consultando ERP..." />
      ) : (
        <ArticuloCard articulo={detalle.data.articulo} />
      )}
    </div>
  );
}

/** Tabla de coincidencias. Cada fila abre la ficha del artículo. */
function ResultadosProductos({
  items,
  total,
  truncado,
  totalMaestro,
  onSeleccionar,
}: {
  items: ArticuloListItem[];
  total: number;
  truncado: boolean;
  totalMaestro: number;
  onSeleccionar: (sku: string) => void;
}) {
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-xs text-gray-500">
          Ningún artículo del maestro ({totalMaestro}) coincide con el filtro.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-xs text-gray-600">
            <b className="text-everex-azul">{total}</b>{' '}
            {total === 1 ? 'coincidencia' : 'coincidencias'} sobre {totalMaestro} artículos
            {truncado && <> · mostrando las primeras {items.length}</>}
          </span>
          <span className="text-[11px] text-gray-500">Click en una fila para ver la ficha</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">SKU</th>
                <th className="px-3 py-2 text-left">Nombre</th>
                <th className="px-3 py-2 text-left">GTIN</th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-left">UM</th>
                <th className="px-3 py-2 text-center">Trazable</th>
              </tr>
            </thead>
            <tbody className="text-gray-800">
              {items.map((a) => (
                <tr
                  key={a.sku ?? a.nombre}
                  onClick={() => a.sku && onSeleccionar(a.sku)}
                  className="cursor-pointer border-t border-gray-100 transition hover:bg-everex-azul-bg"
                >
                  <td className="px-3 py-2 font-mono font-semibold text-everex-azul">
                    {a.sku ?? '—'}
                  </td>
                  <td className="px-3 py-2">{a.nombre ?? '—'}</td>
                  <td className="px-3 py-2 font-mono">{a.codigoDeBarras ?? '—'}</td>
                  <td className="px-3 py-2">{a.tipoArticulo ?? '—'}</td>
                  <td className="px-3 py-2">{a.unidadDeMedida ?? '—'}</td>
                  <td className="px-3 py-2 text-center">
                    {a.trazable ? (
                      <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-800">
                        Sí
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function ArticuloCard({ articulo }: { articulo: ArticuloAcsis }) {
  return (
    <Card>
      <CardContent className="pt-4">
        {/* Header con SKU + nombre */}
        <div className="mb-4 flex flex-wrap items-baseline gap-3 border-b border-gray-100 pb-3">
          <span className="rounded bg-everex-azul/10 px-2 py-1 font-mono text-sm font-bold text-everex-azul">
            {articulo.sku ?? '—'}
          </span>
          <span className="text-base font-semibold text-gray-800">
            {articulo.nombre ?? '—'}
          </span>
          {articulo.tipoArticulo && (
            <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-gray-600">
              {articulo.tipoArticulo}
            </span>
          )}
        </div>

        {/* Datos principales */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs md:grid-cols-3">
          <Dato label="GTIN / Cód. barras" mono>
            {articulo.codigoDeBarras ?? '—'}
          </Dato>
          <Dato label="Articulo interno" mono>
            {articulo.articuloInterno ?? '—'}
          </Dato>
          <Dato label="Unidad de medida">{articulo.unidadDeMedida ?? '—'}</Dato>
          <Dato label="Cuenta contable" mono>
            {articulo.cuentaContable ?? '—'}
          </Dato>
          <Dato label="Fecha de alta">{articulo.fechaAlta?.slice(0, 10) ?? '—'}</Dato>
          <Dato label="Stock mínimo">{articulo.stockMinimo ?? '—'}</Dato>
          <Dato label="Peso embalado">{articulo.pesoEmbalado ?? '—'}</Dato>
          <Dato label="Cant. de bultos">{articulo.cantidadDeBultos ?? '—'}</Dato>
          {articulo.articuloImpresion && articulo.articuloImpresion !== articulo.sku && (
            <Dato label="Art. impresión" mono>
              {articulo.articuloImpresion}
            </Dato>
          )}
        </div>

        {/* Descripción si difiere del nombre */}
        {articulo.descripcion && articulo.descripcion !== articulo.nombre && (
          <div className="mt-3">
            <Dato label="Descripción">{articulo.descripcion}</Dato>
          </div>
        )}

        {/* Flags */}
        <Seccion titulo="Configuración">
          <FlagBadges flags={articulo.flags} />
        </Seccion>

        {/* Clasificadores */}
        <Seccion titulo="Clasificadores">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs md:grid-cols-4">
            {(Object.keys(articulo.clasificadores) as Array<keyof typeof articulo.clasificadores>).map(
              (k) => (
                <Dato key={k} label={k.toUpperCase()} mono>
                  {articulo.clasificadores[k] ?? '—'}
                </Dato>
              ),
            )}
          </div>
        </Seccion>

        {/* Ventas */}
        {articulo.ventas && (
          <Seccion titulo="Ventas">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs md:grid-cols-3">
              <Dato label="Rubro venta">{articulo.ventas.rubroVenta ?? '—'}</Dato>
              <Dato label="Cuenta ventas" mono>
                {articulo.ventas.cuentaVentas ?? '—'}
              </Dato>
              <Dato label="Cant. mín. pedido">
                {articulo.ventas.cantidadMinimaPedido ?? '—'}
              </Dato>
              <Dato label="Bloqueado venta">
                {articulo.ventas.bloqueadoParaVentas ? 'Sí' : 'No'}
              </Dato>
              <Dato label="Prestación">{articulo.ventas.prestacion ? 'Sí' : 'No'}</Dato>
              <Dato label="Es bandeja">{articulo.ventas.esBandeja ? 'Sí' : 'No'}</Dato>
              <Dato label="Es pallet">{articulo.ventas.esPallet ? 'Sí' : 'No'}</Dato>
              <Dato label="Es conjunto">{articulo.ventas.esConjunto ? 'Sí' : 'No'}</Dato>
            </div>
          </Seccion>
        )}
      </CardContent>
    </Card>
  );
}

function FlagBadges({ flags }: { flags: ArticuloAcsis['flags'] }) {
  const items = (Object.keys(flags) as Array<keyof typeof flags>).map((k) => ({
    key: k,
    label: flagLabel(k),
    on: flags[k],
  }));
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it) => (
        <span
          key={it.key}
          className={cn(
            'rounded px-2 py-0.5 text-[10px] font-medium',
            it.on
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-500 line-through',
          )}
          title={it.key}
        >
          {it.label}
        </span>
      ))}
    </div>
  );
}

function flagLabel(key: keyof ArticuloAcsis['flags']): string {
  switch (key) {
    case 'controlaStock': return 'Controla stock';
    case 'conPartidas': return 'Con partidas';
    case 'conNrosDeSerie': return 'Con N° serie';
    case 'conTalles': return 'Con talles';
    case 'seVende': return 'Se vende';
    case 'seCompra': return 'Se compra';
    case 'seProduce': return 'Se produce';
    case 'bloqueoParaMovimientoStock': return 'Bloqueo mov. stock';
    case 'generaMovimientoDeStock': return 'Genera mov. stock';
    case 'conPPP': return 'Con PPP';
  }
}

function Seccion({
  titulo,
  children,
}: {
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4 border-t border-gray-100 pt-3">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {titulo}
      </div>
      {children}
    </div>
  );
}

function Dato({
  label,
  mono = false,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className={cn('mt-0.5 text-gray-800', mono && 'font-mono')}>{children}</div>
    </div>
  );
}
